import React, { useState, useEffect } from "react";
import { adminApi } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Globe,
  Plus,
  Play,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const ScraperPage = () => {
  const [configs, setConfigs] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningScrapers, setRunningScrapers] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scraperResults, setScraperResults] = useState({});
  const [formData, setFormData] = useState({
    product_id: "",
    pricing_page_url: "",
    features_page_url: "",
    scrape_interval_hours: 24,
  });

  const fetchData = async () => {
    try {
      const [configsRes, productsRes] = await Promise.all([
        adminApi.getScraperConfigs(),
        adminApi.getProducts(),
      ]);
      setConfigs(configsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createScraperConfig({
        ...formData,
        scrape_interval_hours: parseInt(formData.scrape_interval_hours),
      });
      toast.success("Scraper config created!");
      setDialogOpen(false);
      setFormData({
        product_id: "",
        pricing_page_url: "",
        features_page_url: "",
        scrape_interval_hours: 24,
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create config");
    }
  };

  const handleRunScraper = async (productId) => {
    setRunningScrapers((prev) => new Set([...prev, productId]));
    try {
      const res = await adminApi.runScraper(productId);
      setScraperResults((prev) => ({ ...prev, [productId]: res.data }));
      
      if (res.data.changes_detected?.length > 0) {
        toast.success(`Changes detected: ${res.data.changes_detected.length} updates`);
      } else {
        toast.info("Scrape complete - no changes detected");
      }
      fetchData();
    } catch (error) {
      toast.error("Scraper failed");
    } finally {
      setRunningScrapers((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const getProductName = (productId) => {
    const product = products.find((p) => p.id === productId);
    return product?.name || "Unknown";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "OK":
        return <CheckCircle size={16} className="text-emerald-400" />;
      case "ERROR":
        return <XCircle size={16} className="text-red-400" />;
      default:
        return <AlertCircle size={16} className="text-yellow-400" />;
    }
  };

  // Get products without scraper configs
  const unconfiguredProducts = products.filter(
    (p) => !configs.find((c) => c.product_id === p.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="scraper-loading">
        <div className="animate-pulse text-[#A1A1AA]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scraper-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Web Scraper</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">
            Monitor competitor pricing and feature updates
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#007AFF] hover:bg-[#007AFF]/80 text-white"
              disabled={unconfiguredProducts.length === 0}
              data-testid="add-scraper-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Scraper
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>Configure Scraper</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-[#A1A1AA]">Product</label>
                <select
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleInputChange}
                  className="w-full bg-[#1A1A1A] border border-white/10 text-white rounded-md p-2 mt-1"
                  required
                  data-testid="scraper-product-select"
                >
                  <option value="">Select product...</option>
                  {unconfiguredProducts.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA]">Pricing Page URL *</label>
                <Input
                  name="pricing_page_url"
                  value={formData.pricing_page_url}
                  onChange={handleInputChange}
                  placeholder="https://example.com/pricing"
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                  required
                  data-testid="scraper-pricing-url-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA]">Features Page URL</label>
                <Input
                  name="features_page_url"
                  value={formData.features_page_url}
                  onChange={handleInputChange}
                  placeholder="https://example.com/features"
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                  data-testid="scraper-features-url-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA]">Scrape Interval (hours)</label>
                <Input
                  name="scrape_interval_hours"
                  type="number"
                  min="1"
                  value={formData.scrape_interval_hours}
                  onChange={handleInputChange}
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                  data-testid="scraper-interval-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#007AFF] hover:bg-[#007AFF]/80"
                data-testid="create-scraper-btn"
              >
                Create Scraper Config
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Scraper Configs */}
      {configs.length === 0 ? (
        <div className="bg-[#121212] border border-white/10 rounded-lg p-12 text-center">
          <Globe size={48} className="mx-auto text-[#A1A1AA] mb-4" />
          <h3 className="text-white text-lg font-semibold">No scrapers configured</h3>
          <p className="text-[#A1A1AA] mt-2">
            Add a scraper to start monitoring competitor prices
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {configs.map((config) => (
            <div
              key={config.id}
              className="bg-[#121212] border border-white/10 rounded-lg p-5"
              data-testid={`scraper-config-${config.product_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-white font-semibold">
                      {getProductName(config.product_id)}
                    </h3>
                    {config.last_run_status && getStatusIcon(config.last_run_status)}
                  </div>
                  <p className="text-[#A1A1AA] text-sm mt-1 font-mono">
                    {config.pricing_page_url}
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-sm text-[#A1A1AA]">
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      Every {config.scrape_interval_hours}h
                    </span>
                    {config.last_run_at && (
                      <span>
                        Last run: {new Date(config.last_run_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {config.last_run_message && (
                    <p className="text-xs text-[#A1A1AA] mt-2 bg-[#1A1A1A] p-2 rounded">
                      {config.last_run_message}
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => handleRunScraper(config.product_id)}
                  disabled={runningScrapers.has(config.product_id)}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/10"
                  data-testid={`run-scraper-${config.product_id}-btn`}
                >
                  {runningScrapers.has(config.product_id) ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play size={16} className="mr-2" />
                      Run Now
                    </>
                  )}
                </Button>
              </div>

              {/* Scraper Results */}
              {scraperResults[config.product_id] && (
                <div className="mt-4 p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
                  <h4 className="text-sm font-semibold text-white mb-2">Latest Results</h4>
                  {scraperResults[config.product_id].data?.pricing?.all_prices?.length > 0 && (
                    <div className="mb-2">
                      <span className="text-xs text-[#A1A1AA]">Detected Prices:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {scraperResults[config.product_id].data.pricing.all_prices.slice(0, 6).map((price, i) => (
                          <span key={i} className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">
                            {price}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {scraperResults[config.product_id].changes_detected?.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs text-yellow-400">Changes Detected:</span>
                      <ul className="mt-1 text-xs text-[#A1A1AA] list-disc list-inside">
                        {scraperResults[config.product_id].changes_detected.map((change, i) => (
                          <li key={i}>{change}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScraperPage;
